/**
 * Auth Error Handler Utility (HA-208)
 *
 * Centralized handling of authentication errors (401) to prevent
 * infinite loops and provide consistent user experience.
 */

import { AxiosError } from 'axios';

import {
  clearLegacyTokens,
  clearServerSession,
  clearUserData,
  endServerSessionInBackground,
  isSigningOut,
} from '../api/sessionAPI';
import { isViewAs, markViewAsEnded } from '../viewAs/viewAsSession';

// Track if we're already handling an auth error to prevent multiple redirects
let isHandlingAuthError = false;

// Track the last time we handled an auth error to prevent rapid-fire handling
let lastAuthErrorTime = 0;
const AUTH_ERROR_DEBOUNCE_MS = 1000;

/**
 * Check if an error is an authentication error (401)
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return error.response?.status === 401;
  }

  return false;
};

/**
 * Check if an error is a forbidden error (403) that's NOT a license denial
 * License denials are handled separately by useLicenseDenialInterceptor
 */
export const isForbiddenError = (error: unknown): boolean => {
  if (error instanceof AxiosError && error.response?.status === 403) {
    // Check if it's a license denial (has allowed: false)
    const data = error.response.data as { allowed?: boolean } | undefined;

    return data?.allowed !== false;
  }

  return false;
};

/**
 * Clear all authentication tokens and cookies
 */
export const clearAuthTokens = (): void => {
  // HA-447: the cookies belong to the admin's own session in their other tab. A view-as tab
  // ends its session instead.
  if (isViewAs()) {
    markViewAsEnded();

    return;
  }

  // HA-445: the session cookie is HttpOnly; the API clears it. The page forgets the session, and
  // any token cookies left from before the switch go too.
  clearServerSession();
  clearLegacyTokens();
  clearUserData();
};

/**
 * Handle authentication errors by clearing tokens and redirecting to login
 * Includes debouncing to prevent multiple simultaneous redirects
 *
 * @param reason - Optional reason to include in the redirect URL
 * @returns true if the error was handled, false if already being handled
 */
export const handleAuthError = (reason: string = 'session_expired'): boolean => {
  // A deliberate sign-out ends the session itself and redirects with its own message; a 401 or a
  // missing session meanwhile is that sign-out, not an expiry.
  if (isSigningOut()) {
    return false;
  }

  const now = Date.now();

  // Debounce: don't handle if we recently handled one
  if (now - lastAuthErrorTime < AUTH_ERROR_DEBOUNCE_MS) {
    return false;
  }

  // Don't handle if already handling
  if (isHandlingAuthError) {
    return false;
  }

  // Don't redirect if already on login page (HA-226: prevent infinite loop)
  const currentPath = window.location.pathname.toLowerCase();

  if (currentPath === '/login' || currentPath === '/LogIn'.toLowerCase()) {
    // Just clear tokens and log - don't redirect
    clearAuthTokens();
    console.warn(`Auth error on login page - tokens cleared, no redirect needed`);

    return false;
  }

  isHandlingAuthError = true;
  lastAuthErrorTime = now;

  // Clear all auth tokens
  clearAuthTokens();
  // HA-445: end the session on the API too, so the sign-in page doesn't send a session the page
  // gave up on straight back in.
  endServerSessionInBackground();

  // Log for debugging (not an error since this is expected behavior)
  console.warn(`Session expired - redirecting to login (reason: ${reason})`);

  // Redirect to login page with reason
  // Use replace to prevent back button from returning to broken state
  window.location.replace(`/login?reason=${encodeURIComponent(reason)}`);

  return true;
};

/**
 * Reset the auth error handling state
 * Call this on successful authentication
 */
export const resetAuthErrorState = (): void => {
  isHandlingAuthError = false;
  lastAuthErrorTime = 0;
};

/**
 * Get the session expired reason from URL params
 * Use this on the login page to show appropriate message
 */
export const getSessionExpiredReason = (): string | null => {
  const params = new URLSearchParams(window.location.search);

  return params.get('reason');
};

/**
 * Check if the current page load is due to session expiry
 */
export const isSessionExpiredRedirect = (): boolean => {
  return getSessionExpiredReason() === 'session_expired';
};
